import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface QuestionResultsListItem {
  id: number;
  subjectName: string;
  matchedField?: string;
}

@Component({
  selector: 'app-question-results-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './question-results-list.component.html',
  styleUrl: './question-results-list.component.css'
})
export class QuestionResultsListComponent {
  @Input() results: QuestionResultsListItem[] = [];
  @Input() displayHtmlById: Record<number, string> = {};
  @Input() badgesById: Record<number, string[]> = {};
  @Input() showMatchedField: boolean = false;
  @Input() emptyMessage: string = 'No results found.';
  @Input() isLoading: boolean = false;
  @Input() selectable: boolean = false;
  @Input() itemsPerPage: number = 20; // Default pagination size
  @Input() showPagination: boolean = true; // Enable/disable pagination

  @Output() selectQuestion = new EventEmitter<number>();

  currentPage: number = 1;

  get totalPages(): number {
    return Math.ceil(this.results.length / this.itemsPerPage);
  }

  get paginatedResults(): QuestionResultsListItem[] {
    if (!this.showPagination) {
      return this.results;
    }
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.results.slice(start, end);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 10); // Show max 10 page numbers
    const startPage = Math.max(1, this.currentPage - 4);
    const endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  // Expose Math to template
  Math = Math;

  badgeClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'red':
      case 'red flag':
        return 'bg-danger';
      case 'yellow':
      case 'yellow flag':
        return 'bg-warning text-dark';
      case 'green':
      case 'green flag':
        return 'bg-success';
      case 'hidden':
        return 'bg-secondary';
      case 'notes':
        return 'bg-info text-dark';
      default:
        return 'bg-secondary';
    }
  }

  onSelect(id: number) {
    if (!this.selectable) return;
    this.selectQuestion.emit(id);
  }
}

